import { type ReactNode, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { OfficeNote as OfficeNoteType } from '@weave/schema-gen-ts/dist/schemas/forms-digital/weave_digital_forms.pb';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Heading, Text } from '@frontend/design-system';
import UnstyledFooterLinks from '../../../layout/unstyled-footer-links.component';
import { OfficeNote } from '../office-note';
import { DynamicForm } from './dynamic-form';
import { MediaAttachment } from './media-upload/types';
import { DynamicFormProvider } from './provider';
import { FormReview } from './review/form-review';
import { SectionProgress } from './section-progress/section-progress';
import { useDynamicFormStore, useNonReactiveDynamicFormStore } from './store';
import { DynamicFormLiveState } from './submit-form.helpers';
import { DynamicFormField, SchemaForm } from './types';

const cardStyle = css`
  background-color: ${theme.colors.white};
  padding: ${theme.spacing(2)};
  border-radius: ${theme.borderRadius.medium};
  box-shadow: ${theme.shadows.floating};
`;

const sectionHeaderStyle = css`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(1)};
  margin-bottom: ${theme.spacing(3)};
`;

const sectionProgressStyle = css`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(1)};
  margin-top: ${theme.spacing(1)};
`;

const autosaveIndicatorStyle = css`
  display: flex;
  align-self: flex-end;
  min-width: 0;
  max-width: 100%;
`;

const getAllFieldValues = (fieldMap: Record<string, DynamicFormField>) => {
  return Object.values(fieldMap).reduce<Record<string, unknown>>((acc, field) => {
    acc[field.key] = field.value;
    return acc;
  }, {});
};

interface DynamicFormWrapperProps {
  onSubmit?: (values: Record<string, unknown>) => void;
  /** Live store state. Only fires from the Review screen's Submit action (never from disableReviewMode). */
  onReviewSubmit?: (liveState: DynamicFormLiveState) => void;
  onValuesChange?: (values: Record<string, unknown>) => void;
  showSectionMeta?: boolean;
  showPreviousButton?: boolean;
  isSubmitting?: boolean;
  disableReviewMode?: boolean;
  nextButtonTitle?: string;
  renderAsCard?: boolean;
  /** Autosave status, rendered right-aligned below the progress bar. */
  renderAutosaveIndicator?: ReactNode;
  officeNote?: OfficeNoteType | null;
}

const DynamicFormWrapperComponent = ({
  onSubmit,
  onReviewSubmit,
  onValuesChange,
  showSectionMeta = true,
  showPreviousButton = true,
  isSubmitting = false,
  disableReviewMode = false,
  nextButtonTitle,
  renderAsCard = true,
  renderAutosaveIndicator,
  officeNote,
}: DynamicFormWrapperProps) => {
  const { t } = useTranslation('digital-forms-experience');
  const { activeSectionIndex, formSections, schemaId, setActiveSectionIndex, setFieldValues } = useDynamicFormStore([
    'activeSectionIndex',
    'formSections',
    'schemaId',
    'setFieldValues',
    'setActiveSectionIndex',
  ]);
  const nonReactiveStore = useNonReactiveDynamicFormStore();
  // Inline editing isn't supported in review (unlike the old field-owned review mode), so this only
  // toggles between the section form and the read-only review screen.
  const [isReviewMode, setIsReviewMode] = useState(false);

  const selectedSection = useMemo(() => formSections[activeSectionIndex], [activeSectionIndex, formSections]);
  const isLastSection = activeSectionIndex === formSections.length - 1;
  const defaultNextButtonText = isLastSection ? (disableReviewMode ? t('Submit') : t('Review')) : t('Next');
  const nextButtonText = nextButtonTitle ?? defaultNextButtonText;
  const previousButtonText = t('Previous');
  const isPreviousDisabled = activeSectionIndex === 0;

  const handleNextClick = (values: Record<string, unknown>) => {
    setFieldValues(values);
    if (!isLastSection) {
      setActiveSectionIndex(activeSectionIndex + 1);
      return;
    }

    // TODO: Validate all sections before review/submission so restored fields cannot be skipped.
    if (disableReviewMode) {
      // disableReviewMode forms (e.g. PatronForm) never reach a Review screen, so only flat values are relevant.
      onSubmit?.(getAllFieldValues(nonReactiveStore.getState().formFieldsMap));
      return;
    }

    setIsReviewMode(true);
  };
  const handlePreviousClick = (values: Record<string, unknown>) => {
    setFieldValues(values);
    setActiveSectionIndex(activeSectionIndex - 1);
  };
  const handleChangeListener = (values: Record<string, unknown>) => {
    setFieldValues(values);
    const allValues = getAllFieldValues(nonReactiveStore.getState().formFieldsMap);
    onValuesChange?.(allValues);
  };
  const handleReviewEdit = (sectionIndex: number) => {
    setIsReviewMode(false);
    setActiveSectionIndex(sectionIndex);
  };
  const handleReviewSubmit = () => {
    onReviewSubmit?.(nonReactiveStore.getState());
  };

  const wrapContent = (content: ReactNode) => {
    const footerLinks = <UnstyledFooterLinks containerCss={{ marginTop: theme.spacing(3) }} />;
    return renderAsCard ? (
      <div css={cardStyle}>
        {content}
        {footerLinks}
      </div>
    ) : (
      <>
        {content}
        {footerLinks}
      </>
    );
  };

  if (isReviewMode) {
    return wrapContent(
      <FormReview onEdit={handleReviewEdit} onSubmit={handleReviewSubmit} isSubmitting={isSubmitting} />
    );
  }

  const formContent = (
    <section>
      {showSectionMeta && (
        <div css={sectionHeaderStyle}>
          <Heading level={2} as='h1' tabIndex={-1} data-dynamic-form-heading>
            {selectedSection.formTitle}
          </Heading>
          {selectedSection.title && <Text color='subdued'>{selectedSection.title}</Text>}
          {formSections.length > 1 && (
            <div css={sectionProgressStyle}>
              <SectionProgress current={activeSectionIndex + 1} total={formSections.length} />
            </div>
          )}
          {renderAutosaveIndicator && <div css={autosaveIndicatorStyle}>{renderAutosaveIndicator}</div>}
          {activeSectionIndex === 0 && <OfficeNote officeNote={officeNote} />}
        </div>
      )}
      <DynamicForm
        key={`${selectedSection.id}-${schemaId}`}
        sectionId={selectedSection.id}
        virtualFieldIds={selectedSection.virtualFieldIds}
        showPreviousButton={showPreviousButton}
        nextButtonText={nextButtonText}
        previousButtonText={previousButtonText}
        prevDisabled={isPreviousDisabled}
        onNext={handleNextClick}
        onPrevious={handlePreviousClick}
        onChangeListener={handleChangeListener}
      />
    </section>
  );

  return wrapContent(formContent);
};

interface DynamicFormContainerProps {
  schemaForms: SchemaForm[];
  initialValues?: Record<string, unknown>;
  initialActiveSectionIndex?: number;
  initialAttachments?: Record<string, MediaAttachment>;
  onSubmit?: (values: Record<string, unknown>) => void;
  onReviewSubmit?: (liveState: DynamicFormLiveState) => void;
  onValuesChange?: (values: Record<string, unknown>) => void;
  showSectionMeta?: boolean;
  showPreviousButton?: boolean;
  isSubmitting?: boolean;
  disableReviewMode?: boolean;
  nextButtonTitle?: string;
  renderAsCard?: boolean;
  renderAutosaveIndicator?: ReactNode;
  officeNote?: OfficeNoteType | null;
  /** Rendered inside the form store provider, before the form (e.g. notices, store bridges). */
  children?: ReactNode;
}

export const DynamicFormContainer = ({
  schemaForms,
  initialValues,
  initialActiveSectionIndex,
  initialAttachments,
  onSubmit,
  onReviewSubmit,
  onValuesChange,
  showSectionMeta,
  showPreviousButton,
  isSubmitting,
  disableReviewMode,
  nextButtonTitle,
  renderAsCard,
  renderAutosaveIndicator,
  officeNote,
  children,
}: DynamicFormContainerProps) => {
  return (
    <DynamicFormProvider
      schemaForms={schemaForms}
      initialValues={initialValues}
      initialActiveSectionIndex={initialActiveSectionIndex}
      initialAttachments={initialAttachments}
    >
      {children}
      <DynamicFormWrapperComponent
        onSubmit={onSubmit}
        onReviewSubmit={onReviewSubmit}
        onValuesChange={onValuesChange}
        showSectionMeta={showSectionMeta}
        isSubmitting={isSubmitting}
        disableReviewMode={disableReviewMode}
        showPreviousButton={showPreviousButton}
        nextButtonTitle={nextButtonTitle}
        renderAsCard={renderAsCard}
        renderAutosaveIndicator={renderAutosaveIndicator}
        officeNote={officeNote}
      />
    </DynamicFormProvider>
  );
};
